import CompleteCameraComuniTurnout from '@/data/affluenza_completa_comuni_camera.json';
import CompleteSenatoComuniTurnout from '@/data/affluenza_completa_comuni_senato.json';
import AdminDivisionsCamera from '@/data/mapping-camera-comuni7917.json';
import AdminDivisionsSenato from '@/data/mapping-senato-comuni7909.json';
import {
  getCoalitionColor,
  getFullNameCandidate,
  getMostraDettaglio,
  getName,
  getPartyColor,
} from '@/lib/candidatesHelpers';
import { getCodCollegio } from '@/lib/convertIdCollegio';
import {
  getAllClUniComune,
  getCodComune,
  getCodComuneFraz,
  getFileNameComune,
} from '@/lib/convertIdComune';
import { formatNumber, parsePercentage } from '@/lib/helpers';
import { getLevelSettings } from '@/lib/levelsHelpers';
import {
  AutocompleteOption,
  DataLevel,
  Result,
  ResultAPI,
  ResultAPIProvincia,
  ResultCodes,
  StructureType,
} from '@/lib/model';
import {
  getAffluenzaCameraNazionaleEleT,
  getAffluenzaCameraNazionalePerc,
  getAffluenzaCameraNazionaleVoti,
  getAffluenzaCameraRegioneEleT,
  getAffluenzaCameraRegionePerc,
  getAffluenzaCameraRegioneVoti,
} from './affluenzaHelper';

const API_URL = `https://2022-real-time-data.s3.eu-central-1.amazonaws.com/`;

export const parseResult = (
  is2018: boolean,
  result: ResultAPI,
  type: StructureType,
  level: DataLevel
) => ({
  type,
  level,
  placeName:
    (level === DataLevel.NAZIONALE
      ? 'Italia'
      : level === DataLevel.COMUNE_FRAZ
      ? result.int.desc_cl_uni
      : level === DataLevel.COMUNE
      ? result.int.desc_com
      : level === DataLevel.COLLEGIO
      ? result.int.desc_cl_uni
      : result.int.desc_circ ?? "Valle d'Aosta") || 'Italia',
  showVoti: !(is2018 && level === DataLevel.COLLEGIO),
  scrutinized: {
    current: result.int.sz_perv,
    total: result.int.sz_tot,
  },
  coalitions: result.cand.map(coalition => ({
    coalitionId: coalition.pos,
    label: getName(coalition, result.int.cod_circ, type, true, is2018),
    fullName: getFullNameCandidate(
      coalition,
      result.int.cod_circ,
      type,
      true,
      result.isMultiCollegio,
      is2018
    ),
    votes: formatNumber(coalition.voti),
    percentage: parsePercentage(coalition.perc),
    fillColor: getCoalitionColor(
      coalition,
      result.int.cod_circ,
      type,
      level,
      is2018
    ),
    candidates: getMostraDettaglio(coalition.pos, type, is2018)
      ? coalition.liste?.map(candidate => ({
          coalitionId: coalition.pos,
          candidateId: candidate.pos,
          label: getName(candidate, result.int.cod_circ, type, false, is2018), // change to this when in PROD -> list.desc_lis,
          fullName: getFullNameCandidate(
            candidate,
            result.int.cod_circ,
            type,
            false,
            result.isMultiCollegio,
            is2018
          ),
          votes: formatNumber(candidate.voti),
          percentage: parsePercentage(candidate.perc),
          fillColor: getPartyColor(candidate.pos, type, is2018),
        }))
      : [],
  })),
  idCodes: {
    codReg: result.int.cod_reg,
    codCirc: result.int.cod_circ,
    codProv: result.int.cod_prov,
    codClPlu: result.int.cod_plu,
    codClUni: result.int.cod_cl_uni,
    codCom: result.int.cod_com,
  },
  summary: {
    turnout: {
      label: 'Affluenza',
      value:
        !is2018 && level === DataLevel.NAZIONALE
          ? `${parsePercentage(getAffluenzaCameraNazionalePerc(type))}%`
          : !is2018 && level === DataLevel.CIRCOSCRIZIONE
          ? !is2018
            ? `${parsePercentage(
                getAffluenzaCameraRegionePerc(Number(result.int.cod_reg), type)
              )}%`
            : `${parsePercentage(result.int.perc_vot)}%`
          : `${parsePercentage(result.int.perc_vot)}%`,
    },
    totalElectors: {
      label: 'Totale Elettori',
      value:
        !is2018 && level === DataLevel.NAZIONALE
          ? formatNumber(getAffluenzaCameraNazionaleEleT(type))
          : !is2018 && level === DataLevel.CIRCOSCRIZIONE
          ? !is2018
            ? formatNumber(
                getAffluenzaCameraRegioneEleT(Number(result.int.cod_reg), type)
              )
            : formatNumber(result.int.ele_t)
          : formatNumber(result.int.ele_t),
    },
    totalVoters: {
      label: 'Totale Votanti',
      value:
        !is2018 && level === DataLevel.NAZIONALE
          ? formatNumber(getAffluenzaCameraNazionaleVoti(type))
          : !is2018 && level === DataLevel.CIRCOSCRIZIONE
          ? !is2018
            ? formatNumber(
                getAffluenzaCameraRegioneVoti(Number(result.int.cod_reg), type)
              )
            : formatNumber(result.int.vot_t)
          : formatNumber(result.int.vot_t),
    },
    blankCards: {
      label: 'Schede Bianche',
      value: formatNumber(result.int.sk_bianche),
    },
    contestedCards: {
      label: 'Schede Contestate',
      value: formatNumber(result.int.sk_contestate),
    },
    nullCards: {
      label: 'Schede Nulle',
      value: formatNumber(result.int.sk_nulle),
    },
  },
  is2018: is2018,
});

export const parseResultToMapLayer = (
  result: ResultAPI,
  type: StructureType,
  level: DataLevel,
  is2018: boolean
) => {
  const { variable } = getLevelSettings(level);

  // we use the first candidate as the winning one,
  // because the list is ordered from highest to lowest percentage.
  const winningCandidate = result.cand[0];

  let idFromVariable: any = result.int[variable]; // default
  let color = '#D9D9D9'; // default se voti 0 o perc null
  if (winningCandidate === undefined) {
    console.log('check', result);
  } else if (winningCandidate.voti > 0 && winningCandidate.perc !== null) {
    color = getCoalitionColor(
      winningCandidate,
      result.int.cod_circ,
      type,
      level,
      is2018
    );
  }

  if (level === DataLevel.COLLEGIO) {
    idFromVariable = getCodCollegio(idFromVariable, type);
  } else if (level === DataLevel.COMUNE) {
    idFromVariable = getCodComune(
      result.int.cod_reg,
      result.int.cod_prov,
      result.int.cod_com
    );
  } else if (level === DataLevel.COMUNE_FRAZ) {
    idFromVariable = getCodComuneFraz(
      type,
      result.int.cod_reg,
      result.int.cod_prov,
      result.int.cod_com,
      result.int.cod_cl_uni
    );
  } else if (level === DataLevel.CIRCOSCRIZIONE) {
    if (type === StructureType.SENATE) {
      if (idFromVariable === '21') {
        // trasform "cod_circ":"21" in COD_REG: 4 for TRENTINO
        idFromVariable = '4';
      }
    }
    if (isNaN(idFromVariable)) {
      idFromVariable = '2'; // VDA
    }
  }

  return {
    fillColor: color,
    opacity: 1, // full color of the winning coalition // parsePercentage(winningCandidate?.perc) / 100,
    layerId: idFromVariable,
    coordinates: [],
  };
};

const fetchResults = async (
  is2018: boolean,
  type: StructureType,
  level: DataLevel,
  codes?: ResultCodes,
  uniqueId?: boolean
): Promise<ResultAPI | ResultAPI[] | null> => {
  const directoryType = type === StructureType.CAMERA ? 'camera' : 'senato';
  const { directory } = getLevelSettings(level);

  const electionsFolder = is2018 ? 'elections_2018' : 'latest_2022';

  let url = `${API_URL}${electionsFolder}/${directoryType}`;
  let urlsMultiCollegi: string[] = [];
  if (level === DataLevel.NAZIONALE) {
    url += `/${type === StructureType.CAMERA ? '02' : '03'}.json`;
  } else if (
    (level === DataLevel.COMUNE || level === DataLevel.COMUNE_FRAZ) &&
    codes
  ) {
    // find by unique filename
    if (!is2018 && uniqueId) {
      let arrCodesUni = getAllClUniComune(
        type,
        codes.codReg,
        codes.codProv,
        codes.codCom
      );
      if (arrCodesUni.length > 1) {
        arrCodesUni.forEach(codUni => {
          codes.codClUni = codUni;
          let urlM = `${API_URL}${directoryType}`;
          let fileNameM = getFileNameComune(type, codes);
          urlM += `/${fileNameM}.json`;
          urlsMultiCollegi.push(urlM);
        });
      } else {
        const fileName = getFileNameComune(type, codes);

        url += `/${fileName}.json`;
      }
    } else {
      // find all comuni within collegio
      url += `_comune_by_collegio_${codes.codClUni}.json`;
    }
  } else {
    url += `_${directory}.json`;
  }

  if (urlsMultiCollegi.length > 0) {
    return Promise.all(
      urlsMultiCollegi.map(async url => {
        return await (await fetch(url)).json().catch(err => {
          return null;
        });
      })
    ).then(combined => {
      let firstNotNull = combined.filter((cc: ResultAPI) => cc !== null);
      if (firstNotNull.length === 0) {
        return null;
      }
      let c1: ResultAPI = firstNotNull[0];
      c1.isMultiCollegio = true;

      combined.forEach((cc: ResultAPI) => {
        if (cc !== null && cc.int.cod_cl_uni !== c1.int.cod_cl_uni) {
          c1.int.ele_t += cc.int.ele_t;
          c1.int.sk_bianche += cc.int.sk_bianche;
          c1.int.sk_contestate += cc.int.sk_contestate;
          c1.int.sk_nulle += cc.int.sk_nulle;
          c1.int.sz_perv += cc.int.sz_perv;
          c1.int.sz_tot += cc.int.sz_tot;
          c1.int.tot_vot_uni += cc.int.tot_vot_uni;

          let votiValidiCoalizione =
            c1.int.tot_vot_uni -
            c1.int.sk_bianche -
            c1.int.sk_contestate -
            c1.int.sk_nulle;
          c1.int.perc_vot = ((c1.int.tot_vot_uni / c1.int.ele_t) * 100).toFixed(
            2
          );

          let totaleVotiSoloCan = 0;
          c1.cand.forEach(cand => {
            let cand2: any = cc.cand.filter(c => c.pos === cand.pos)[0];
            totaleVotiSoloCan += cand.voti_solo_can ?? 0;
            totaleVotiSoloCan += cand2.voti_solo_can ?? 0;
          });

          c1.cand.forEach(cand => {
            let cand2: any = cc.cand.filter(c => c.pos === cand.pos)[0];
            cand.voti += cand2.voti;
            cand.voti_solo_can += cand2.voti_solo_can;
            if (cand.voti > 0 && votiValidiCoalizione > 0) {
              cand.perc = ((cand.voti / votiValidiCoalizione) * 100).toFixed(2);
            } else {
              cand.perc = '0';
            }

            cand.liste?.forEach(lista => {
              let lista2 = cand2.liste?.filter(
                (l: any) => l.pos === lista.pos
              )[0];
              lista.voti += lista2.voti;
              if (
                lista.voti > 0 &&
                votiValidiCoalizione - totaleVotiSoloCan > 0
              ) {
                lista.perc = (
                  (lista.voti / (votiValidiCoalizione - totaleVotiSoloCan)) *
                  100
                ).toFixed(2);
              } else {
                lista.perc = '0';
              }
            });
          });
        }
      });

      c1.cand.sort((a, b) => b.voti - a.voti);

      return c1;
    });
  } else {
    try {
      let res: ResultAPI | ResultAPI[] = await (await fetch(url))
        .json()
        .catch(err => {
          return null;
        });

      if (level === DataLevel.COMUNE) {
        const isArray = Array.isArray(res);
        const firstResult = (
          isArray ? (res as ResultAPI[])[0] : res
        ) as ResultAPI;
        const sourceData =
          type === StructureType.CAMERA
            ? CompleteCameraComuniTurnout
            : CompleteSenatoComuniTurnout;
        const provinceTurnout: any = (sourceData as ResultAPIProvincia[]).find(
          t => t.enti.ente_p.desc === firstResult.int.desc_prov
        );

        res = ((isArray ? res : [res]) as ResultAPI[]).map(result => {
          const comuneTurnout = provinceTurnout?.enti.enti_f.find(
            (c: any) => c.desc === result.int.desc_com
          );

          if (!comuneTurnout) {
            return result;
          }

          // com_vot[0]: ore 12 camera / ore 23 senato
          // com_vot[1]: ore 19 solo camera
          // com_vot[2]: ore 23 solo camera
          let indexCom = type === StructureType.CAMERA ? 2 : 0;
          const turnoutResults = comuneTurnout.com_vot[indexCom];

          return {
            ...result,

            int: {
              ...result.int,
              perc_vot: turnoutResults.perc,
              vot_t: turnoutResults.vot_t,
            },
          };
        });
      }

      if (level === DataLevel.COMUNE_FRAZ) {
        if (codes !== undefined) {
          res = (res as ResultAPI[]).filter(
            (c: any) =>
              (c.int.cod_circ === codes.codCirc ||
                c.int.cod_reg === codes.codReg) &&
              c.int.cod_prov === codes.codProv &&
              c.int.cod_com === codes.codCom &&
              c.int.cod_cl_uni === codes.codClUni &&
              c.int.cod_plu === codes.codClPlu
          );
        } else {
          res = (res as ResultAPI[]).filter(
            (c: any) => c.int.cod_cl_uni === '111' || c.int.cod_cl_uni === '112'
          );
        }
      }
      return res;
    } catch {
      return null;
    }
  }
};

export const getResults = async (
  is2018: boolean,
  type: StructureType,
  level: DataLevel,
  codes?: ResultCodes,
  uniqueId?: boolean
): Promise<Result[]> => {
  let data = await fetchResults(is2018, type, level, codes, uniqueId);
  if (!data) {
    return [];
  }

  data = Array.isArray(data) ? data : [data];

  if (level === DataLevel.CIRCOSCRIZIONE && type === StructureType.SENATE) {
    if (Array.isArray(data)) {
      let trentinoReg = data.filter(
        r => r.int.cod_circ === '21' && r.int.l_terr === 'CIRCOSCRIZIONE'
      )[0];

      if (trentinoReg !== undefined) {
        let mappaPartitiTrentino: Map<string, any> = new Map();

        let votiValidi = 0;

        if (data !== null && Array.isArray(data) && data.length > 0) {
          data.forEach((res: any) => {
            if (
              res.int.cod_circ === '21' &&
              res.int.l_terr !== 'CIRCOSCRIZIONE'
            ) {
              votiValidi +=
                res.int.tot_vot_uni -
                res.int.sk_bianche -
                res.int.sk_nulle -
                res.int.sk_contestate;

              res.cand.forEach((cand: any) => {
                if (cand.desc_contr !== undefined) {
                  if (!mappaPartitiTrentino.has(cand.desc_contr)) {
                    mappaPartitiTrentino.set(cand.desc_contr, cand);
                  } else {
                    const candEsistente: any = mappaPartitiTrentino.get(
                      cand.desc_contr
                    );
                    candEsistente.voti += cand.voti;
                  }
                }
              });
            }
          });
          mappaPartitiTrentino.forEach((partito: any, key: string) => {
            if (partito.voti > 0 && votiValidi > 0) {
              partito.perc = ((partito.voti / votiValidi) * 100).toFixed(2);
            } else {
              partito.perc = '0';
            }
            trentinoReg.cand.push(partito);
          });
          trentinoReg.cand.sort(({ voti: a }, { voti: b }) => b - a);
        }
      }

      // filtro tutte le circoscrizioni + VDA (collegio uni)
      data = data.filter(
        r => r.int.l_terr === 'CIRCOSCRIZIONE' || '2' === r.int.cod_circ
      );
    }
  }

  return data.map((result: ResultAPI) => ({
    ...parseResult(is2018, result, type, level),
    mapLayer: parseResultToMapLayer(result, type, level, is2018),
  }));
};

export const getAutocompleteOptions = (type: StructureType) => {
  const data = (
    type === StructureType.CAMERA ? AdminDivisionsCamera : AdminDivisionsSenato
  ) as AutocompleteOption[];

  return data
    .reduce((acc: AutocompleteOption[], el) => {
      if (
        acc.some(
          option =>
            option.cod_comune === el.cod_comune &&
            option.cod_provincia === el.cod_provincia
        )
      ) {
        return acc;
      }

      return [...acc, el];
    }, [])
    .sort((a, b) => {
      // should be:
      // a.cod_provincia.localeCompare(b.cod_provincia);

      return a.nome_provincia.localeCompare(b.nome_provincia);
    });
};

export const getLastVersion = async () => {
  return await (
    await fetch(
      `https://2022-real-time-data.s3.eu-central-1.amazonaws.com/latest_2022/last_version.json`
    )
  ).json();
};
